import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Clock, Users, Award, ChevronRight, ExternalLink } from 'lucide-react';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from '../components/PaginationControls';
import PolytechnicSlideshowBackground from '../components/PolytechnicSlideshowBackground'
interface College {
  name: string;
  location: string;
  duration: string;
  established: string;
  cutoff: string;
  university: string;
  type: string;
  website?: string;
  notes?: string;
}

const PolytechnicPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('Government');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Government', 'Private', 'Aided', 'Autonomous', 'Affiliated'];

  const colleges: Record<string, College[]> = {
    Government: [
      {
        name: "Institute of Printing Technology",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1960",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.printingtechnologychennai.in"
      },
      {
        name: "Institute of Chemical Technology",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.ictchennai.in"
      },
      {
        name: "Institute of Leather Technology",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.iltchennai.in"
      },
      {
        name: "Institute of Textile Technology",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.textileinstitute.in"
      },
      {
        name: "State Institute of Commerce Education",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1970",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "http://www.sicecollege.in"
      },
      {
        name: "T.P.E.V.R Government Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "tpevrgptc.com"
      },
      {
        name: "Government Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptckgiri.org"
      },
      {
        name: "Government Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1946",
        cutoff: "Tamil Nadu State Board: 60+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptccbe.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "The Nilgiris, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcooty.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcaranthangi.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptctrichy.com"
      },
      {
        name: "Alagappa Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1960",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.agptckaraikudi.in"
      },
      {
        name: "Government Polytechnic College, Tuticorin",
        location: "Thoothukudi, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gpctuticorin.com"
      },
      {
        name: "Government Polytechnic College Nagercoil",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptnagercoil.com"
      },
      {
        name: "Government Polytechnic College, Purasawalkam",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gpcpurasai.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcmelur.com"
      },
      {
        name: "Government Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcdharmapuri.co.in"
      },
      {
        name: "Government Polytechnic College Karur",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcknp.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcperambalur.in"
      },
      {
        name: "Government Polytechnic College, Tiruvarur",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptckorukkai.in"
      },
      {
        name: "Government Polytechnic College, Theni",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptctheni.co.in"
      },
      {
        name: "Government Polytechnic College, Thiruvannamalai",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptctvm.in"
      },
      {
        name: "Government Polytechnic College, Arakandanallure",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcvillupuram.in"
      },
      {
        name: "Government Polytechnic College, Andipatti",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcaundipatti.com"
      },
      {
        name: "Government Polytechnic College, Jolarpattai",
        location: "Tirupattur, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptcjolarpet.org"
      },
      {
        name: "Government Polytechnic College, Kilapalure",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "2003",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcariyalur.com"
      },
      {
        name: "Government Polytechnic College, Perundurai",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2004",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcperundurai.co.in"
      },
      {
        name: "Government Polytechnic College, Thirumangalam",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2005",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcchekkanurani.com"
      },
      {
        name: "Government Polytechnic College, Sankarapuram",
        location: "Kallakurichi, Tamil Nadu",
        duration: "3 years",
        established: "2006",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcsankarapuram.edu.in"
      },
      {
        name: "Government Polytechnic College, Cheyyar",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2007",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcheyyar.in"
      },
      {
        name: "Government Polytechnic College, Srirangam",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2008",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcsrirangam.com"
      },
      {
        name: "Government Polytechnic College, Gandharvakottai",
        location: "Pudukottai, Tamil Nadu",
        duration: "3 years",
        established: "2009",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcgkt.in"
      },
      {
        name: "Government Polytechnic College, Usilampatti",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2010",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcusilai.org"
      },
      {
        name: "Government Polytechnic College, Uthangarai",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2011",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptuthangarai.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2012",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptcrkn.edu.in"
      },
      {
        name: "Government Polytechnic College, Boomandahalli",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2013",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptckadathur.org"
      },
      {
        name: "Government Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2014",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcklm.co.in"
      },
      {
        name: "Government Polytechnic College, Reghunathapuram",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2015",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptctanjore.page.tl"
      },
      {
        name: "Government Polytechnic College, Thozhuvoor",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2016",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcvalangai.com"
      },
      {
        name: "Government Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2017",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcpalacode.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcmohanur.com"
      },
      {
        name: "Government Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptckooduveli.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gptcsevvapet.edu.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptcvanavasi.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Chengalpattu, Tamil Nadu",
        duration: "3 years",
        established: "2022",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "gptchromepet.co.in"
      },
      {
        name: "Government Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2023",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "https//irtbargur.org/"
      },
      {
        name: "Government Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2024",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government"
      },
      {
        name: "Government Polytechnic College",
        location: "Thiruvottriyur, Tamil Nadu",
        duration: "3 years",
        established: "2024",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government"
      },
      {
        name: "Dr Dharmambal Government Polytechnic College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.ddgpcwchennai.com"
      },
      {
        name: "Government Polytechnic College for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gpcwcbe.edu.in"
      },
      {
        name: "Government Polytechnic College for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.gpcwmdu.co.in"
      },
      {
        name: "Bharathiyar Centenary Memorial Government Women's Polytechnic College",
        location: "Thoothukudi, Tamil Nadu",
        duration: "3 years",
        established: "2006",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government",
        website: "www.bcmwgovtpolytechnic.com"
      },
      {
        name: "APJ Abdul Kalam Government Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2015",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Government"
      }
    ],
    Private: [
      {
        name: "V.Ramakrishna Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Meenakshi Ammal Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Nallalaghu Nadar Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Adhiparasakthi Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Arulmigu Thirupurasundari Amman Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sriram Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Valliammai Polytechnic College",
        location: "Chengalpattu, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Saint Michael's Polytechnic College",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Meenakshi Krishnan Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Swamy Abedhanandha Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pandiyan Polytechnic College",
        location: "Tirupattur, Tamil Nadu",
        duration: "3 years",
        established: "2003",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Elumalai Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2004",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sankara Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Narayana Guru Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nanjiah Lingammal Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nanjappa Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kongu Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Erode Institute of Chemical Technology Polytechnic College",
        location: "Nadupatti, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thiru Ramakrishna Nallammai Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Al-Ameen Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Annai J.K.K.Sampoorani Ammal Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Salem Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Muthayammal Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K.S.Rangasamy Institute of Technology",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "CSI Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Subramanian Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "A V C Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shanmugha Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "C C M Ramakrishna Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Karur Polytechnic College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thanthai Roever Institute of Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.I.E.T. Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K.L.Nagaswamy Memorial Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ratnavel Subramaniam Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Christian Institute for Tech Education (Polytechnic College)",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thangam-Muthu Polytechnic College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Arulmigu Kallalakar Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thanapandiyan Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Seikalathur Kamatchi Amman Polytechnic College Manamadurai",
        location: "Sivaganga, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Arulmigu Kalasalingam Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ramu-Seetha Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Seethaiammal Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Sowdambika Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pasumpon Nethaji Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Lakshmi Ammal Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S.A.Raja's Polytechnic College",
        location: "Vadakkankulam, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Gomathi Ambal Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St Xavier's Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Morning Star Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Noorul Islam Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkateswara Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thai Moogambigai Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Christ The King Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Rajaji Institute of Technology",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Srinivasa Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "GMS-Mavmm Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2003",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Park AMC Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2004",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Arulmigu Senthilandavar Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2005",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.S.P.Velayutha Nadar Lakshmithai Ammal Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2006",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kalaivanar N.S.K Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2007",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thirumathi Elizabeth Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2008",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pallavan Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2009",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Annai Velankanni Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2010",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Panimalar Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2005",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thiru Seven Hills Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2011",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "C P C L Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2012",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Moderator Gnanandason Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2013",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "N A Manjammal Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2008",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Krishna Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2009",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Adhiyamaan Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2010",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Bharath Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2011",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vignesh Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2012",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Balaji Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2013",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dr M.G.R Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2014",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Krishna Polytechnic College",
        location: "Ranipet, Tamil Nadu",
        duration: "3 years",
        established: "2015",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ranipet Institute of Technology",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2016",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pee Gee Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2017",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Siga Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2004",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Hindustan Institute of Engineering Technology Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2005",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sivakasi Institute of Printing Technology",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2006",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jayaraj Annapackiam CSI Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Sai Ram Polytechnic College, Chennai",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 58+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private",
        website: "www.sairampoly.edu.in"
      },
      {
        name: "S.A. Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2000",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S.R.M. Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "P S B Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2002",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kumaran Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2003",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Priyadarshini Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2004",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Maruthi Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2005",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Er.Perumal Manimekalai Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2006",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Rudhraveni Muthuswamy Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2007",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2008",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkateswara Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2009",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Latha Mathavan Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2010",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aalim Muhammed Salegh Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2011",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dr.B.R.Ambedkar Polytechnic College",
        location: "Yanam, Tamil Nadu",
        duration: "3 years",
        established: "2012",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Venkatesvara Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2013",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Krishnasamy Memorial Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2014",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sun Institute of Technology",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2015",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SCAD Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2016",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Muthalamman Polytechnic College",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "2017",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Suguna Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Murugesan Institute of Technology",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ragavendra Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K.S.R Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ayyappa Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Tirupattur Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sembodai R.V Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kar Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Infant Jesus Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Krishna Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Paavai Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Padalesuwarar Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Raja Desingh Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SBM Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Anndavar Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Little Flower Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Durgadevi Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Laxminarayana Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S M Arumugam Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vivekananda Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Kavery Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S.R.I. Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aries Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PGP Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nandha Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dhanalakshmi Srinivasan Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Bharath Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkatachalapathy Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Cheyyar Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Muruga Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Tiruttani Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "DPC Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "P V Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Samuel Polytechnic College",
        location: "Thoothukudi, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dusi Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shri Sitheswarar Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "John Bosco Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Adhisankarar Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Lalgudi Co-operative Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pastor Lenssen Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jaya Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Udaya Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "J.J Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ponnaiyah Ramajayam Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vandayar Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Apollo Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Grace Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Mahendra Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.A.R Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PA Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "R V S Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "T S Srinivasan Centre for Polytechnic College and Advanced G",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Surya Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "FX Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sun Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Mother Terasa Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sakthi Institute of Fashion Technology",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sudharsan Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aravindar Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "A R J Polytechnic College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Arulmurugan Polytechnic College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Chendhuran Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Kongu Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Konghu Velalar Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.A.M. Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nagasiva Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "P S R Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shreenivasa Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Vengateswaraa Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Srinivasan Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Kavery Institute of Technology",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Maharani Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vetri Vinayaha Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aarooran Polytechnic College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Elumalaiyan Polytechnic College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Maisurii Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kongunadu Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Meenaakshi Ramasamy Polytechnic College",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The New Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pavendar Bharathidasan Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SRG Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Bharathi Velu Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sudharshana Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Theni Kammavar Sangam Polytechnic College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Renga Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "N P R Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkateshwaraa Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Surabi College of Polytechnic",
        location: "Ranipet, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "National Institute of Polytechnic",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PSN Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Royal Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Udhayam Polytechnic College",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Cape Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Musiri Institute of Technology",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Indian Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PSN Institute of Technology",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Rajiv Gandhi Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Bhavani Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Karthikeya Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Erode Kongu College of Polytechnic",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Gandhi Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Salem Kongu Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kottai Mariyamman Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Excel Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Jay Maruthi Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Paavendhar Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Rengeswarar Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Voice of God Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Balakrishna Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Narasimma Pallavan Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Cholan Institute of Technology",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Gudiyattam Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramachandra Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Rajalakshmi Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Annamalaiyar Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Renugambal Polytechnic College",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dhivya Polytechnic College",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "A.K.T Memorial Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ayyappa Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sasthaa Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Mahath Amma Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St Joseph Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Bharathi Polytechnic College for Women",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jayaram Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Imayam Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Annai College of Polytechnic",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thiruvalluvar Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Balamurugan Polytechnic College",
        location: "Palani, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jayam Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "N.V. Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thiruvalluvar Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ranganathar Institute of Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sir Issac Newton Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Haji Sheik Ismail Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Don Bosco Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vivekananda Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Michael Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "The Kevin Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PET Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Udaya Institute of Technology",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Union Christian Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vickram Polytechnic College",
        location: "Sivaganga, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Adiparasakthi Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S. Veerasamy Chettiar Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "C.M. Annamalai Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S.S.K. Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Karpaga Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SVS Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Guru Raagavindra Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Marutamnelli Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "MIT Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "KKS Mani Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Paramveer Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri S. Ramasamy Naidu Memorial Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "NM Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkatesvara Polytechnic College",
        location: "Kaikuruchi, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vigneshwara Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Mariam Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Kumaraguru Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Padmam Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Vinayaga Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Vignesh Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramana Institute of Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Joseph Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aishwarya Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Santhoshi Polytechnic Colleges",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Don Bosco Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vethathiri Maharishi Institute of Technology",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Nandhanam Polytechnic College",
        location: "Tirupattur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thirumalai Madhanur Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Sakthi Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Joseph's Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Annamalai Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Surya Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Arumugham Polytechnic College Tholudur",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Annai Madha Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nethaji Subash Chandra Bose Polytechnic College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shree Venkateshwara Hitech Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shri Krishnaa Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Acharya Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Thiagarajar Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Subabharathi Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K.M.G Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "U.S.P. Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "BWDA Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nachiappa Swamigal Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "VJP Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "MNSK Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "G P Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Newcape Polytechnic College",
        location: "Aralvaimozhi, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thirumalai Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Muthayammal Polytechnic Institution",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Raasi Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Vari Mayasudhan College of Polytechnic",
        location: "Parthibanur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SMS Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Prince Polytechnic College",
        location: "Mulanur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S. Thangapazham Polytechnic College",
        location: "Tenkasi, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K E T Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Adhithya Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kalapandian Polytechnic College",
        location: "Bodinayakanur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Shri Ramanas ABC Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Devangar Polytechnic College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Mahalakshmi Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Mannora Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Annai Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Hi-Tech Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SVA College of Polytechnic",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kumaran Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jeya Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thaai Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Meenakshi Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Amma Polytechnic College",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Golden Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jeeva's Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "RECT Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Al Islamiya Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "VKP Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Dr. Kalam Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "TJS Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Bharath Niketan Polytechnic College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St Xavier's Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Swami Vivekananda Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sree Vaikundar Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "MRK Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Tirumala Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vivekananda Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Joseph's Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Maharaja Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ranganathan Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "PSV Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.M Polytechnic College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vellore Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "J.R. Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Everest Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kongunadu Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vaani Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SSR Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Pothigai Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "CMS Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "K.S.M. Polytechnic College",
        location: "Manamadurai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Hindusthan Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "SPECT Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Annai Abhirami Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Vatsa Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Lakshmisubramanian Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kapi Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sadasivam Kathirkamavalli Polytechnic College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Girivaasan Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kamatchi Polytechnic College, Pothavur",
        location: "Pothavur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thevanesam Erudhaya Ammal Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Venkatramana Polytechnic College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Venkateswara Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Angel Polytechnic College",
        location: "Rajapalayam, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Maria Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "E.G.S Pillay Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Rane Polytechnic Technical Campus",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ramya Sathianathan Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Merit Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Cheran Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sita Rajaram Polytechnic College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Ramachandra Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jeevan Polytechnic College",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Varadharajan Polytechnic College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kumaran Polytechnic College",
        location: "Thiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Senthur Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "S. Padmavathy Polytechnic College",
        location: "Thuraiyur, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Parimala Pandurangan Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Moogambigai Polytechnic College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Aravindhar Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Nadar Mahajana Sangam Vimala Chelladurai Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "E.S Polytechnic College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kongu Hi-Tek Polytechnic College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Suthanthira Polytechnic College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Thirumagal Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "St. Jude's Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ka So Ka Polytechnic College",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Modern Polytechnic College",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Immanuel Arasar JJ College of Engineering (Shift)",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "P.S.V Polytechnic College",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "P.S.G. Ponnammal Polytechnic College",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Kamarajar Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "R.V. Reha Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Ammaiappar Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Meenatchi Govindarajan Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Jai Krishna Polytechnic College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sriboghar Polytechnic College",
        location: "Panthanenthal, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Veludaiyar Polytechnic College",
        location: "Ammaiyappan, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Vethamonickam Memorial CSI Polytechnic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "JCT Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Omm Muruga Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Andal Nachiyar Polytechnic College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Easwar Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Don Bosco Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "M.S. Polytechnic College",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "2018",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Perunthalaivar K Kamarajar Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "2019",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Professional Group of Institutions, Tiruppur (Shift)",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "2020",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      },
      {
        name: "Sri Rangapoopathi College of Engineering, Villupuram (Shift)",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "2021",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Private"
      }
    ],
    Aided: [
      {
        name: "P T Lee Chengalvaraya Naicker Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.ptleecnpt.in"
      },
      {
        name: "Ramakrishna Mission Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1962",
        cutoff: "Tamil Nadu State Board: 60+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.rkmshome.org"
      },
      {
        name: "Bhaktavatsalam Polytechnic College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided"
      },
      {
        name: "AMK Technological Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.amkpolytechnic.com"
      },
      {
        name: "Rajagopal Polytechnic College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.rpg.ac.in"
      },
      {
        name: "Muthiah Polytechnic College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.muthiahpolytechniccollege.com"
      },
      {
        name: "CIT Sandwich Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "Tamil Nadu State Board: 58+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.citspc.edu.in"
      },
      {
        name: "Sri Krishna Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.skptc.srikrishna.ac.in"
      },
      {
        name: "EIT Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.eitpolytech.in"
      },
      {
        name: "Sakthi Polytechnic College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.sakthitech.net"
      },
      {
        name: "SSM Polytechnic College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.ssmtech.org"
      },
      {
        name: "Valivalam Desikar Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.vdpcollege.com"
      },
      {
        name: "Pattukkottai Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.ppc-india.org"
      },
      {
        name: "Arulmigu Palaniandavar Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.palaniandavarpc.org.in"
      },
      {
        name: "Rukmini Shanmugam Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided"
      },
      {
        name: "Annamalai Polytechnic College",
        location: "Sivaganga, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.annamalaipolytechnic.in"
      },
      {
        name: "Mohamed Sathak Polytechnic College",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided"
      },
      {
        name: "Arasan Ganesan Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.arasanganesanpoly.org"
      },
      {
        name: "Kamaraj Polytechnic College",
        location: "Kanniyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.nmskamarajpolytechnic.com"
      },
      {
        name: "NPA Centenary Polytechnic College",
        location: "The Nilgiris, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.npakotagiri.com"
      },
      {
        name: "Ayya Nadar Janaki Ammal Polytechnic College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.anja-pc.org"
      },
      {
        name: "A.D.J.Dharmambal Polytechnic College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.adjdpc.ac.in",
        notes: "Government Aided"
      },
      {
        name: "Srinivasa Subbaraya Polytechnic College",
        location: "Mayiladuthurai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 45+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Aided",
        website: "www.ssgptc.in"
      }
    ],
    Autonomous: [
      {
        name: "Central Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1956",
        cutoff: "Tamil Nadu State Board: 60+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "cptchennai.ac.in"
      },
      {
        name: "Tamilnadu Polytechnic College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1948",
        cutoff: "Tamil Nadu State Board: 60+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.tamilnadupolytechnicmadurai.com"
      },
      {
        name: "Murugappa Polytechnic College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1968",
        cutoff: "Tamil Nadu State Board: 58+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.murugappapoly.ac.in"
      },
      {
        name: "PSG Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1951",
        cutoff: "Tamil Nadu State Board: 65+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.psgpolytech.ac.in"
      },
      {
        name: "Sri Ramakrishna Mission Vidyalaya Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "Tamil Nadu State Board: 60+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.srkvtech.org"
      },
      {
        name: "Nachimuthu Polytechnic College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.nptc.ac.in"
      },
      {
        name: "Thiagarajar Polytechnic College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1957",
        cutoff: "Tamil Nadu State Board: 62+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.tpt.edu.in"
      },
      {
        name: "Seshasayee Institute of Technology",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous"
      },
      {
        name: "V.S.V.N. Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.vsvnpolytechnic.in"
      },
      {
        name: "P.A.C. Ramasamy Raja Polytechnic College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.pacrpoly.org"
      },
      {
        name: "Sankar Polytechnic College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous"
      },
      {
        name: "Periyar Centenary Polytechnic College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "Tamil Nadu State Board: 48+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Autonomous",
        website: "www.periyarpolytech.com"
      }
    ],
    Affiliated: [
      {
        name: "Government Technical Training Centre",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "Tamil Nadu State Board: 55+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Affiliated"
      },
      {
        name: "Institute of Ceramic Technology",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Affiliated"
      },
      {
        name: "Institute of Tool Engineering",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "Tamil Nadu State Board: 52+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Affiliated"
      },
      {
        name: "Regional Labour Institute",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "Tamil Nadu State Board: 50+%",
        university: "Directorate of Technical Education, Tamil Nadu",
        type: "Affiliated"
      }
    ]
  };

  const filteredColleges = colleges[selectedCategory]?.filter(college =>
    college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    college.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Pagination using custom hook
  const pagination = usePagination({
    data: filteredColleges,
    initialItemsPerPage: 100
  });

  // Reset pagination when category or search changes
  React.useEffect(() => {
    pagination.resetPagination();
  }, [selectedCategory, searchTerm]);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const TableView = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cutoff</th>
            
            
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagination.currentItems.map((college, index) => (
              <motion.tr
                key={index}
                className="hover:bg-gray-50"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
              >
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{college.name}</div>
                    <div className="text-sm text-gray-500">Est. {college.established}</div>
                    {college.notes && (
                      <div className="text-xs text-orange-600 mt-1">{college.notes}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.location}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    college.type === 'Government' 
                      ? 'bg-green-100 text-green-800' 
                      : college.type === 'Aided'
                      ? 'bg-yellow-100 text-yellow-800'
                      : college.type === 'Autonomous'
                      ? 'bg-purple-100 text-purple-800'
                      : college.type === 'Affiliated'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {college.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.cutoff}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                 
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100">
      {/* Hero Section */}
      <section className="relative text-white py-16 sm:py-20 overflow-hidden" style={{ minHeight: 400 }}>
        <PolytechnicSlideshowBackground />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Polytechnic Colleges in Tamil Nadu
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-orange-100 max-w-3xl mx-auto">
              Explore top polytechnic colleges offering Diploma programs in Engineering and Technology with comprehensive information about admission requirements and official websites.
            </p>
          </motion.div>
        </div>
      </section>

      {/* TNEA Admission Status Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: TNEA 2025 Admissions were closed on 15/09/2025</span>
            <span>•</span>
            <span>📞 For guidance on alternative admissions and next steps, contact our experts</span>
            <span>•</span>
            <span>🎓 We help you find the best alternative pathways</span>
            <span>•</span>
            <span>📱 Contact: +91 77086 77132 (Enquiry) | +91 93610 27132 (Advisor)</span>
            <span>•</span>
            <span>📧 Email: infonammasewa@gmail.com</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search colleges by name or location..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                    selectedCategory === category
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Results Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-6"
        >
          <p className="text-gray-600">
            Showing <span className="font-semibold text-orange-600">{pagination.startIndex + 1}-{Math.min(pagination.endIndex, filteredColleges.length)}</span> of <span className="font-semibold text-orange-600">{filteredColleges.length}</span> polytechnic colleges in <span className="font-semibold">{selectedCategory}</span> category
            {pagination.totalPages > 1 && (
              <span className="ml-2 text-sm text-gray-500">(Page {pagination.currentPage} of {pagination.totalPages})</span>
            )}
          </p>
        </motion.div>

        {/* College Table */}
        <TableView />

        {/* Pagination Controls */}
        <PaginationControls
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          itemsPerPage={pagination.itemsPerPage}
          totalItems={filteredColleges.length}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          onNextPage={pagination.handleNextPage}
          onPrevPage={pagination.handlePrevPage}
          onItemsPerPageChange={pagination.setItemsPerPage}
          onPageChange={pagination.goToPage}
          className="mt-8"
        />

        {filteredColleges.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-500 text-lg">No colleges found matching your criteria.</div>
          </motion.div>
        )}
      </div>

      {/* Information Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">About Polytechnic Education</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Polytechnic education provides diploma courses in engineering and technology fields. These 3-year programs 
              focus on practical training and skill development, preparing students for technical careers in industry.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Clock,
                title: "Duration",
                description: "3 years diploma program",
                color: "orange"
              },
              {
                icon: Award,
                title: "Eligibility",
                description: "10th pass with Science & Math",
                color: "green"
              },
              {
                icon: Users,
                title: "Admission",
                description: "Merit-based selection",
                color: "purple"
              },
              {
                icon: Users,
                title: "Career Scope",
                description: "Technical jobs, higher studies",
                color: "blue"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                  <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolytechnicPage;